import { Heart, HeartOff, ListMusic, ListPlus, ListStart, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useBulkFavorite } from '@/api/hooks'
import type { LibraryItem } from '@/api/types'
import { toQueueItem, usePlayer, type QueueItem } from '@/audio/store'
import {
  AddToPlaylistSheet,
  SelectionBar,
  type Selection,
  type SelectionAction,
} from '@/components/selection'
import { toast } from '@/components/ui'

import { BulkRemoveDialog } from './BulkRemoveDialog'
import { errorMessage } from './errorMessage'
import { trackCountLabel } from './labels'

export interface LibrarySelectionBarProps {
  /** Every row currently on screen, in display order. */
  items: LibraryItem[]
  selection: Selection<number>
  /** Leaves selection mode. Called once an action has finished. */
  onExit: () => void
}

/**
 * The bulk actions for a library list: the docked bar plus the two surfaces it
 * opens. Mounted by whoever owns the list, so the songs list and an album's
 * track list behave identically.
 */
export function LibrarySelectionBar({ items, selection, onExit }: LibrarySelectionBarProps) {
  const [playlistOpen, setPlaylistOpen] = useState(false)
  const [removeIds, setRemoveIds] = useState<number[] | null>(null)

  const bulkFavorite = useBulkFavorite()
  const enqueue = usePlayer((state) => state.enqueue)
  const playNextUp = usePlayer((state) => state.playNextUp)

  // Ids that scrolled out of the list — or were filtered away — simply stop
  // counting; acting on a row nobody can see would be worse than dropping it.
  const selected = useMemo(
    () => items.filter((item) => selection.isSelected(item.id)),
    [items, selection],
  )

  const ids = useMemo(() => selected.map((item) => item.id), [selected])
  const trackIds = useMemo(() => selected.map((item) => item.track.id), [selected])
  const allFavorite = selected.length > 0 && selected.every((item) => item.is_favorite)

  function playableEntries(): QueueItem[] {
    return selected
      .map(toQueueItem)
      .filter((entry): entry is QueueItem => entry !== null)
  }

  /** Some of a selection is normally still downloading. Say which, don't hide it. */
  function skippedNote(playing: number): string | undefined {
    const skipped = selected.length - playing
    if (skipped === 0) return undefined
    return `${skipped} ${skipped === 1 ? 'track is' : 'tracks are'} not downloaded yet and ${skipped === 1 ? 'was' : 'were'} left out.`
  }

  function addToPlayer(mode: 'next' | 'queue') {
    const entries = playableEntries()
    if (entries.length === 0) {
      toast.error(
        'Nothing to play yet',
        'None of the selected songs have finished downloading.',
      )
      return
    }
    if (mode === 'next') playNextUp(entries)
    else enqueue(entries)
    toast.info(
      mode === 'next'
        ? `Playing ${trackCountLabel(entries.length)} next`
        : `Added ${trackCountLabel(entries.length)} to the queue`,
      skippedNote(entries.length),
    )
    onExit()
  }

  function toggleFavorites() {
    const favorite = !allFavorite
    bulkFavorite.mutate(
      { ids, favorite },
      {
        onSuccess: (result) => {
          toast.success(
            favorite
              ? `Added ${trackCountLabel(result.updated)} to favourites`
              : `Removed ${trackCountLabel(result.updated)} from favourites`,
          )
          onExit()
        },
        onError: (cause) => toast.error('Could not update favourites', errorMessage(cause)),
      },
    )
  }

  const actions: SelectionAction[] = [
    {
      key: 'playlist',
      label: 'Add to playlist',
      icon: ListMusic,
      onSelect: () => setPlaylistOpen(true),
    },
    { key: 'next', label: 'Play next', icon: ListStart, onSelect: () => addToPlayer('next') },
    { key: 'queue', label: 'Add to queue', icon: ListPlus, onSelect: () => addToPlayer('queue') },
    {
      key: 'favorite',
      label: allFavorite ? 'Remove from favourites' : 'Add to favourites',
      icon: allFavorite ? HeartOff : Heart,
      loading: bulkFavorite.isPending,
      onSelect: toggleFavorites,
    },
    {
      key: 'remove',
      label: 'Remove from library',
      icon: Trash2,
      destructive: true,
      onSelect: () => setRemoveIds(ids),
    },
  ]

  return (
    <>
      <SelectionBar count={selected.length} onClear={onExit} actions={actions} />

      <AddToPlaylistSheet
        open={playlistOpen}
        onOpenChange={setPlaylistOpen}
        trackIds={trackIds}
        onAdded={onExit}
      />

      {removeIds ? (
        <BulkRemoveDialog
          ids={removeIds}
          onClose={() => setRemoveIds(null)}
          onRemoved={onExit}
        />
      ) : null}
    </>
  )
}
